import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Modal,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import CustomRankingInput from "../../components/formComponent/CustomRankingInput";
import CustomSelect from "../../components/formComponent/CustomSelect";
import {
  addRecommendation,
  updateRecommendation,
} from "../../services/observationsApi";
import { useState } from "react";
import { toast } from "react-toastify";

type AddRecommendationModalProps = {
  isEdit: boolean;
  recommendationData: any;
  open: boolean;
  onClose: () => void;
  onSave: (data?: any) => void;
  clinicUniqueId: string;
  dateFilter: {
    startDate: string;
    endDate: string;
  };
};

const AddRecommendationModal: React.FC<AddRecommendationModalProps> = ({
  isEdit,
  recommendationData,
  open,
  onClose,
  onSave,
  clinicUniqueId,
  dateFilter,
}) => {
  const { handleSubmit, control, reset } = useForm<any>({
    defaultValues: {
      category: "",
      description: "",
      priority: "Medium",
    },
  });

  useEffect(() => {
    if (recommendationData && isEdit) {
      reset({
        category: recommendationData?.category || "",
        description: recommendationData?.description || "",
        priority: recommendationData?.priority || "Medium",
      });
    } else {
      reset({
        category: "",
        description: "",
        priority: "Medium",
      });
    }
  }, [recommendationData]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (data: any) => {
    console.log(data, "clinicUniqueId");
    setIsLoading(true);
    setError(null);

    try {
      // Extract month and year from endDate
      const endDate = new Date(dateFilter.endDate);
      const month = endDate.getMonth() + 1; // getMonth() returns 0-11
      const year = endDate.getFullYear();

      const api = isEdit ? updateRecommendation : addRecommendation;

      const response = await api(clinicUniqueId, {
        ...(recommendationData?.id && { id: recommendationData?.id }),
        category: data.category,
        description: data.description,
        priority: data.priority,
        month: month.toString(),
        year: year.toString(),
      });

      if (response.status.msg === "Success") {
        toast.success("Recommendation added successfully");
        onSave(response);
        onClose();
        reset();
      }
    } catch (err) {
      setError("Failed to save recommendation. Please try again.");
      console.error("Error saving recommendation:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset(); // Reset the form when closing
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-recommendation-modal-title"
      aria-describedby="add-recommendation-modal-description"
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          backgroundColor: "#fff",
          borderRadius: "10px",
          outline: "none",
        }}
      >
        <div
          style={{
            backgroundColor: "#194e5b",
            color: "white",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            padding: "16px",
            fontSize: "1.25rem",
            fontWeight: "500",
          }}
          id="add-recommendation-modal-title"
        >
          {isEdit ? "Edit Recommendation" : "Add New Recommendation"}
        </div>
        <div
          style={{ padding: "16px" }}
          id="add-recommendation-modal-description"
        >
          <Controller
            name="category"
            control={control}
            defaultValue=""
            rules={{ required: "category is required" }}
            render={({ field, fieldState: { error } }) => (
              <CustomRankingInput
                label="Category"
                placeholder="e.g. Website"
                field={field}
                helperText="The area this recommendation relates to"
                fieldState={
                  {
                    error: error
                      ? { type: error.type, message: error.message }
                      : undefined,
                  } as any
                }
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            rules={{
              required: "description is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <CustomRankingInput
                placeholder="Describe your recommendation..."
                type="textarea"
                label="Description"
                helperText=""
                field={field}
                fieldState={
                  {
                    error: error
                      ? { type: error.type, message: error.message }
                      : undefined,
                  } as any
                }
              />
            )}
          />
          <div className="px-6" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <CustomSelect
              name="priority"
              control={control}
              label="Priority"
              options={[
                { value: "High", label: "High" },
                { value: "Medium", label: "Medium" },
                { value: "Low", label: "Low" },
              ]}
              rules={{ required: "Please select a priority" }}
              className="w-full "
            />
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handleClose}
            className="btn btn-sm"
            style={{
              backgroundColor: "#c9eeeb",
              color: "#0E3843",
              marginRight: "8px",
            }}
          >
            Cancel
          </button>
          <button
            disabled={isLoading}
            onClick={handleSubmit(handleSave)}
            style={{ backgroundColor: "#0E3843" }}
            className="btn btn-sm bg-[#0E3843] rounded-md text-white border hover:bg-[#0A2A30]"
          >
            {isLoading ? "Saving..." : "Save Recommendation"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddRecommendationModal;
